<template>
  <div class="list-container">
    <div class="top-fix-area" ref="topFixArea">
      <h1 class="page-title">顧客情報一覧</h1>
      <div class="mb-2">
        <router-link v-if="isAdmin" :to="{ path: '/create-customer', query: getSearchQuery() }" class="btn btn-sm btn-primary me-2">
          顧客情報作成
        </router-link>

        <button v-if="isAdmin" type="button" class="btn btn-sm btn-primary me-2" data-bs-toggle="modal" data-bs-target="#exampleModal">
          CSVインポート
        </button>

        <button type="button" class="btn btn-sm btn-primary me-2" @click="downloadCsv">
          CSVエクスポート
        </button>

        <button class="btn btn-sm btn-outline-secondary" @click="isSearchExpanded = !isSearchExpanded">
          {{ isSearchExpanded ? '検索条件を非表示' : '検索条件を表示' }}
        </button>
      </div>

      <div class="modal fade" id="exampleModal" data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-lg">
          <div class="modal-content">
            <div class="modal-header">
              <h1 class="modal-title fs-5">CSVインポート</h1>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" @click="clearErrorMessages"></button>
            </div>
            <div class="modal-body">
              <div v-if="errorMessages">
                <div class="error-massage-item" v-for="errorMessage in errorMessages" :key="errorMessage.id">
                  <p class="alert alert-danger mt-2">{{ errorMessage.row_num }}行目：{{ errorMessage.messages[0] }}</p>
                </div>
              </div>

              <div class="my-3">
                <p>顧客情報CSVファイル選択</p>
                <input id="customerCsvFileInput" type="file" class="form-control" @change="handleFileChange">
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="clearErrorMessages">閉じる</button>
              <button type="button" class="btn btn-primary" @click="submitFile">インポート</button>
            </div>
          </div>
        </div>
      </div>

      <SearchPanelWrapper :key="route.fullPath" v-model="isSearchExpanded" :getTableElement="() => tableWrapper?.$el || tableWrapper?.value">
        <form class="search-container" @submit.prevent="searchCustomers">
          <div class="row g-3 align-items-center">
            <div class="col-auto">
              <label for="searchName" class="col-form-label">顧客名</label>
            </div>
            <div class="col-auto">
              <input id="searchName" v-model="searchName" type="text" class="form-control" placeholder="顧客名で検索">
            </div>

            <DeletedStatusSelect v-model="searchDeletedStatus" id="customerDeletedStatus" />

            <div class="col-auto">
              <button type="submit" class="btn btn-sm btn-primary mx-1">検索</button>
              <button type="button" class="btn btn-sm btn-secondary mx-1" @click="searchResetCustomers">クリア</button>
            </div>
          </div>
        </form>
      </SearchPanelWrapper>
    </div>

    <div class="d-flex justify-content-end" ref="paginationContainer">
      <Pagination
        :currentPage="currentPage"
        :totalPages="totalPages"
        @change-page="handleChangePage"
      />
    </div>

    <div class="table-wrapper" ref="tableWrapper">
      <table class="master-table table table-bordered">
        <thead class="table-dark">
          <tr>
            <th class="text-nowrap">ID</th>
            <th class="text-nowrap">顧客名</th>
            <th class="text-nowrap">郵便番号</th>
            <th class="text-nowrap">住所</th>
            <th class="text-nowrap">電話番号</th>
            <th class="text-nowrap">FAX</th>
            <th class="text-nowrap">代表者</th>
            <th class="btn-cell"></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="customer in customers" :key="customer.id" :class="{ 'no-active-customer': customer.del_flg == 1 }">
            <td>{{ customer.id }}</td>
            <td>{{ customer.customer_nm }}</td>
            <td class="text-nowrap">{{ customer.postal_code }}</td>
            <td>{{ customer.prefecture }}{{ customer.city }}{{ customer.other_address }}</td>
            <td class="text-nowrap">{{ customer.tel_num }}</td>
            <td class="text-nowrap">{{ customer.fax }}</td>
            <td class="text-nowrap">{{ customer.president_nm }}</td>
            <td class="btn-cell text-nowrap">
              <router-link :to="{ name: 'CustomerDetail', params: { id: customer.id }, query: getSearchQuery() }">
                <button type="button" class="btn btn-sm btn-info">詳細</button>
              </router-link>

              <router-link v-if="isAdmin" :to="{ name: 'CustomerEdit', params: { id: customer.id }, query: getSearchQuery() }">
                <button type="button" class="btn btn-sm btn-success mx-2">編集</button>
              </router-link>

              <button
                v-if="customer.del_flg == 0 && isAdmin"
                class="btn btn-sm btn-danger"
                data-bs-toggle="modal"
                data-bs-target="#deleteModal"
                @click="handleDeleteClick(customer.id)"
              >
                削除
              </button>
              <button
                v-if="customer.del_flg == 1 && isAdmin"
                class="btn btn-sm btn-warning"
                data-bs-toggle="modal"
                data-bs-target="#restoreModal"
                @click="handleRestoreClick(customer.id)"
              >
                復元
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <DeleteConfirmationModal
      :paramsId="paramsIdToDelete"
      @confirmDelete="handleConfirmDelete"
    />

    <RestoreConfirmationModal
      :paramsId="paramsIdToRestore"
      @confirmRestore="handleConfirmRestore"
    />
  </div>
</template>

<script>
import { ref, onMounted, watchEffect, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import {
  getCustomers,
  deleteCustomer,
  restoreCustomer,
  exportCustomersCsv,
  importCustomersCsv
} from '@/api/axios';
import { Modal } from 'bootstrap';
import SearchPanelWrapper from '@/components/searchPanels/SearchPanelWrapper.vue';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal.vue';
import RestoreConfirmationModal from '@/components/shared/RestoreConfirmationModal.vue';
import Pagination from '@/components/shared/Pagination.vue';
import DeletedStatusSelect from '@/components/shared/DeletedStatusSelect.vue';
import { currentRoleFromStoreOrLocalStorage, isAdminRole } from '@/utils/roleAuthorization';

export default {
  components: {
    SearchPanelWrapper,
    DeleteConfirmationModal,
    RestoreConfirmationModal,
    Pagination,
    DeletedStatusSelect
  },
  setup() {
    const title = ref('顧客情報一覧');

    watchEffect(() => {
      document.title = title.value;
    });

    const store = useStore();
    const isAdmin = computed(() => isAdminRole(currentRoleFromStoreOrLocalStorage(store.state.userRole)));
    const router = useRouter();
    const route = useRoute();

    const customers = ref([]);
    const selectedFile = ref(null);
    const errorMessages = ref({});
    const searchName = ref('');
    const searchDeletedStatus = ref('active');

    const paramsIdToDelete = ref(null);
    const paramsIdToRestore = ref(null);

    const currentPage = ref(1);
    const totalPages = ref(1);

    const isSearchExpanded = ref(true);
    const topFixArea = ref(null);
    const tableWrapper = ref(null);
    const paginationContainer = ref(null);

    const getSearchQuery = () => ({
      customer_name: searchName.value,
      customer_deleted_status: searchDeletedStatus.value,
      customer_page: currentPage.value || 1
    });

    const fetchCustomers = async (page = 1) => {
      try {
        const response = await getCustomers({
          name: searchName.value,
          deleted_status: searchDeletedStatus.value,
          include_visibility_hidden: true,
          page
        });

        customers.value = response.data.customers || [];
        currentPage.value = response.data.meta?.current_page || 1;
        totalPages.value = Math.max(response.data.meta?.total_pages || 1, 1);
      } catch (error) {
        console.error('顧客情報データの取得に失敗しました:', error);
      }
    };

    const handleChangePage = async (page) => {
      if (page < 1 || page > totalPages.value) return;

      await fetchCustomers(page);

      const q = getSearchQuery();
      q.customer_page = page;
      router.replace({ path: '/customers', query: q });

      if (tableWrapper.value) {
        tableWrapper.value.scrollTop = 0;
      }
    };

    const handleDeleteClick = (customerId) => {
      paramsIdToDelete.value = customerId;
    };

    const handleRestoreClick = (customerId) => {
      paramsIdToRestore.value = customerId;
    };

    const handleConfirmDelete = async (customerId) => {
      try {
        await deleteCustomer(customerId, { include_visibility_hidden: true });
        store.commit('setFlashMessage', { message: '顧客情報が削除されました', type: 'success' });
        await fetchCustomers(currentPage.value);
      } catch (error) {
        store.commit('setFlashMessage', { message: '顧客情報削除に失敗しました', type: 'danger' });
      }
    };

    const handleConfirmRestore = async (customerId) => {
      try {
        await restoreCustomer(customerId, { include_visibility_hidden: true });
        store.commit('setFlashMessage', { message: '顧客情報が復元されました', type: 'success' });
        await fetchCustomers(currentPage.value);
      } catch (error) {
        store.commit('setFlashMessage', { message: '顧客情報復元に失敗しました', type: 'danger' });
      }
    };

    const downloadCsv = async () => {
      try {
        const response = await exportCustomersCsv({
          name: searchName.value,
          deleted_status: searchDeletedStatus.value,
          include_visibility_hidden: true
        });

        const now = new Date();
        const timestamp = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}_${now.getHours().toString().padStart(2, '0')}${now.getMinutes().toString().padStart(2, '0')}${now.getSeconds().toString().padStart(2, '0')}`;
        const fileName = `m_customers_${timestamp}.csv`;

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('CSVダウンロードエラー', error);
      }
    };

    const handleFileChange = (event) => {
      selectedFile.value = event.target.files[0];
    };

    const closeModal = () => {
      const modalElement = document.getElementById('exampleModal');
      const modal = Modal.getInstance(modalElement) || new Modal(modalElement);
      modal.hide();
    };

    const clearErrorMessages = () => {
      errorMessages.value = {};
      selectedFile.value = null;

      const input = document.getElementById('customerCsvFileInput');
      if (input) {
        input.value = '';
      }
    };

    const submitFile = async () => {
      if (!selectedFile.value) {
        store.commit('setFlashMessage', { message: 'ファイルが選択されていません', type: 'danger' });
        return;
      }

      try {
        await importCustomersCsv(selectedFile.value);
        store.commit('setFlashMessage', { message: 'アップロードに成功しました', type: 'success' });
        closeModal();
        clearErrorMessages();
        await fetchCustomers(currentPage.value);
      } catch (error) {
        if (error.response && error.response.data) {
          errorMessages.value = error.response.data || {};
        }
        console.error('アップロードに失敗しました', error);
        store.commit('setFlashMessage', { message: 'アップロードに失敗しました', type: 'danger' });
      }
    };

    const searchCustomers = async () => {
      await fetchCustomers(1);

      const q = getSearchQuery();
      q.customer_page = 1;
      router.push({ path: '/customers', query: q });
    };

    const searchResetCustomers = async () => {
      searchName.value = '';
      searchDeletedStatus.value = 'active';
      await fetchCustomers(1);

      const q = getSearchQuery();
      q.customer_page = 1;
      router.replace({ path: '/customers', query: q });
    };

    onMounted(() => {
      const q = route.query;
      searchName.value = q.customer_name || '';
      searchDeletedStatus.value = q.customer_deleted_status || 'active';
      currentPage.value = q.customer_page ? Number(q.customer_page) : 1;
      fetchCustomers(currentPage.value);
    });

    return {
      title,
      isAdmin,
      route,
      customers,
      paramsIdToDelete,
      paramsIdToRestore,
      handleDeleteClick,
      handleRestoreClick,
      handleConfirmDelete,
      handleConfirmRestore,
      downloadCsv,
      selectedFile,
      handleFileChange,
      submitFile,
      errorMessages,
      clearErrorMessages,
      searchName,
      searchDeletedStatus,
      getSearchQuery,
      currentPage,
      totalPages,
      handleChangePage,
      searchCustomers,
      searchResetCustomers,
      isSearchExpanded,
      topFixArea,
      tableWrapper,
      paginationContainer
    };
  }
};
</script>

<style scoped>
.list-container {
  padding: 20px 20px 80px 20px;
  margin: auto;
}

.table-wrapper {
  overflow: auto;
  max-height: calc(100vh - 320px);
}

.master-table .btn-cell {
  width: 240px;
  min-width: 240px;
  max-width: 240px;
}

.no-active-customer td {
  background-color: #aaa;
}
</style>
